import { useEffect } from 'react';
import { HiExclamationCircle, HiCheckCircle, HiInformationCircle, HiExclamation, HiX } from 'react-icons/hi';

const icons = {
  error: HiExclamationCircle,
  success: HiCheckCircle,
  warning: HiExclamation,
  info: HiInformationCircle,
};

const styles = {
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-600',
    button: 'bg-red-500 hover:bg-red-600 shadow-red-500/25',
    glow: 'shadow-red-500/10',
  },
  success: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-500',
    title: 'text-emerald-800',
    message: 'text-emerald-600',
    button: 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/25',
    glow: 'shadow-emerald-500/10',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-500',
    title: 'text-amber-800',
    message: 'text-amber-600',
    button: 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/25',
    glow: 'shadow-amber-500/10',
  },
  info: {
    bg: 'bg-primary-50',
    border: 'border-primary-200',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-500',
    title: 'text-primary-800',
    message: 'text-primary-600',
    button: 'bg-primary-500 hover:bg-primary-600 shadow-primary-500/25',
    glow: 'shadow-primary-500/10',
  },
};

export default function AlertDialog({ isOpen, onClose, type = 'error', title, message }) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const s = styles[type] || styles.error;
  const Icon = icons[type] || icons.error;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fadeIn"
        onClick={onClose}
      />

      <div className={`
        relative w-full max-w-sm bg-white rounded-3xl shadow-2xl ${s.glow}
        border ${s.border}
        animate-[popIn_0.35s_ease-out_forwards]
        overflow-hidden
      `}>
        <div className={`absolute top-0 left-0 right-0 h-1.5 ${s.button}`} />

        <button
          onClick={onClose}
          className="absolute top-4 left-4 p-1.5 rounded-xl hover:bg-gray-100 transition-colors z-10"
        >
          <HiX className="w-4 h-4 text-gray-400" />
        </button>

        <div className="px-6 pt-8 pb-6 flex flex-col items-center text-center">
          <div className={`
            w-16 h-16 rounded-2xl ${s.iconBg} ${s.border} border
            flex items-center justify-center mb-4
            animate-[wiggle_0.5s_ease-in-out]
          `}>
            <Icon className={`w-8 h-8 ${s.iconColor}`} />
          </div>

          {title && (
            <h3 className={`text-lg font-bold ${s.title} mb-2`}>{title}</h3>
          )}

          <p className={`text-sm leading-relaxed ${s.message}`}>{message}</p>
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className={`
              w-full py-3 rounded-xl text-white font-semibold text-sm
              ${s.button} shadow-lg
              transition-all duration-200 active:scale-[0.97]
            `}
          >
            حسناً
          </button>
        </div>
      </div>
    </div>
  );
}
